import React, { useState, useRef, useEffect } from 'react';
import { Upload, Printer, Save, Search, Loader2, Plus, Minus, Calendar, DollarSign, Sparkles, FileText } from 'lucide-react';
import type { PrintableRef, Currency } from '@/types';
import PrintableDocument from '@/components/PrintableDocument';
import QuotationsService from '@/api/quotations.service';
import { DocumentUpload } from '@/components/DocumentUpload';
import DocumentProcessingService from '@/api/documentProcessing.service';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import ProductServicesService from '@/api/productservices.service';
import BillToSection from '@/components/BillToSection';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { QuotationTaxSection } from '@/components/tax/QuotationTaxSection';
import TaxesService from '@/api/taxes.service';

// Update the Customer type to match the schema
type Customer = {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  street: string;
  city: string;
  state: string;
  zipCode?: string; // Optional
  country: string;
  companyName?: string; // Optional
};

type ProductService = {
  id: number;
  productServiceId: number;
  name: string;
  description: string;
  unitPrice: number;
};

// Update the Tax interface to match our new components
interface Tax {
  id: number;
  taxName: string;
  taxRate: number;
  country?: string;
  type?: string;
  code?: string;
  taxAmount?: number;
}

export default function DocumentForm() {
  const [logo, setLogo] = useState<string>();
  const [signature, setSignature] = useState<string>();
  const [currency, setCurrency] = useState<Currency>('USD');
  const [items, setItems] = useState([{ productServiceId: 0, name: '', description: '', quantity: 1, unitPrice: 0 }]);
  const [status] = useState('pending');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    emailAddress: '',
    phoneNumber: '',
    logoImg: ''
  });
  const [customerDetails, setCustomerDetails] = useState<Customer>({
    id: 0,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    companyName: ''
  });
  const [customerData, setCustomerData] = useState<Customer[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [isInclusive, setIsInclusive] = useState(false);
  const [quotationNumber, setQuotationNumber] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [expiryDate, setExpiryDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  });
  const printableRef = useRef<PrintableRef>(null);

  const [searchProduct, setSearchProduct] = useState('');
  const [isSearchingProduct, setIsSearchingProduct] = useState(false);
  const [productData, setProductData] = useState<ProductService[]>([]);
  
  const [isSaving, setIsSaving] = useState(false);

  const { toast } = useToast();
  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const [businessTaxInfos, setBusinessTaxInfos] = useState<any[]>([]);
  const [customerTaxInfos, setCustomerTaxInfos] = useState<any[]>([]);

  // AI Processing state
  const [showAiTab, setShowAiTab] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [isApplyingExtraction, setIsApplyingExtraction] = useState(false);

  useEffect(() => {
    if (selectedBusiness) {
      setCompanyDetails({
        name: selectedBusiness.name,
        address: selectedBusiness.address,
        city: selectedBusiness.city,
        country: selectedBusiness.country,
        emailAddress: selectedBusiness.emailAddress,
        phoneNumber: selectedBusiness.phoneNumber,
        logoImg: selectedBusiness.logoImg
      });
      setLogo(selectedBusiness.logoImg);
      
      // Fetch business tax information
      fetchBusinessTaxInfos(selectedBusiness.id);
    }
  }, [selectedBusiness]);

  // Watch for changes to customerDetails.id and fetch customer tax info
  useEffect(() => {
    if (customerDetails?.id > 0) {
      fetchCustomerTaxInfos(customerDetails.id);
    }
  }, [customerDetails?.id]);

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignature(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrint = () => {
    if (printableRef.current) {
      printableRef.current.print();
    }
  };

  const fetchBusinessTaxInfos = async (businessId: number) => {
    try {
      const response = await TaxesService.getBusinessTaxInfos(businessId);
      if (response.data.result === 'success') {
        setBusinessTaxInfos(response.data.businessTaxInfos || []);
      }
    } catch (error) {
      console.error('Error fetching business tax information:', error);
    }
  };

  const fetchCustomerTaxInfos = async (customerId: number) => {
    try {
      const response = await TaxesService.getCustomerTaxInfos(customerId);
      if (response.data.result === 'success') {
        setCustomerTaxInfos(response.data.customerTaxInfos || []);
      }
    } catch (error) {
      console.error('Error fetching customer tax information:', error);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    if (!(companyDetails && customerDetails)) {
      showToastWithTimeout("error", "❌ Quotation Creation Failed", "Please fill in all required fields.", 3000);
      setIsSaving(false);
      return;
    }

    try {
      const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0) || 0;
      const taxAmount = taxes.reduce((sum, tax) => sum + (tax.taxAmount || 0), 0);
      const total = subtotal + taxAmount;

      const newQuotation = {
        customerId: customerDetails.id, // Use the selected or added customer's ID
        amount: subtotal,
        total: total,
        currency,
        status,
        issueDate,
        expiryDate,
        items: items.map(item => ({
          productServiceId: item.productServiceId,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalPrice: item.quantity * item.unitPrice,
        })),
        taxes: taxes.map(tax => ({
          taxName: tax.taxName,
          taxRate: tax.taxRate,
          taxAmount: tax.taxAmount,
        })),
        isInclusive,
        companyDetails,
        customerDetails,
        notes,
        terms,
        logo,
        signature,
        businessTaxInfos,  // Add business tax information to the quotation data
        customerTaxInfos  // Add customer tax information to the quotation data
      };

      const response = await QuotationsService.createQuotation(newQuotation, selectedBusiness.id);
      console.log("createQuotation response.data", response.data);
      if (response.data.result === "success") {
        showToastWithTimeout("success", "✅ Quotation Created", "Quotation created successfully", 3000);
        setQuotationNumber(response.data.quotationNumber);
      } else {
        showToastWithTimeout("error", "❌ Quotation Creation Failed", response.data.message, 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "❌ Quotation Creation Failed", "An error occurred while creating the quotation.", 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTaxesChange = (newTaxes: Tax[]) => {
    setTaxes(newTaxes);
  };

  const handleInclusiveChange = (newIsInclusive: boolean) => {
    setIsInclusive(newIsInclusive);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  const calculateTotalTax = () => {
    return taxes.reduce((sum, tax) => sum + (tax.taxAmount || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTotalTax();
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchProduct.length > 0) {
        handleSearchProduct();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchProduct]);

  const handleSearchProduct = async () => {
    setIsSearchingProduct(true);
    try {
      const response = await ProductServicesService.searchProductService(selectedBusiness.id, searchProduct);
      const { data } = response;
      if (data.result === 'success') {
        setProductData(data.productServices);
        showToastWithTimeout("success", "✅ Product Search Successful", "Product search successful", 3000);
      } else {
        showToastWithTimeout("error", "❌ Product Search Failed", data.message, 3000);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSearchingProduct(false);
    }
  };

  // AI Processing handlers
  const handleExtractionComplete = (data: any) => {
    setExtractedData(data);
    setShowAiTab(true);
    showToastWithTimeout("success", "✨ AI Extraction Complete", "Document data has been extracted successfully. Review the extracted data in the 'Extracted Data' tab.", 5000);
  };

  const applyExtractedData = () => {
    if (!extractedData?.structuredData) return;
    
    setIsApplyingExtraction(true);
    const data = extractedData.structuredData;

    try {
      // Map vendor/customer information
      if (data.vendorName || data.customerName) {
        const name = data.vendorName || data.customerName;
        const nameParts = name.split(' ');
        setCustomerDetails(prev => ({
          ...prev,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          companyName: data.vendorName || data.customerName || ''
        }));
      }

      // Map quotation details
      if (data.invoiceNumber || data.quotationNumber) {
        setQuotationNumber(data.invoiceNumber || data.quotationNumber);
      }

      if (data.invoiceDate || data.quotationDate) {
        const dateStr = data.invoiceDate || data.quotationDate;
        if (dateStr) {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            setIssueDate(date.toISOString().split('T')[0]);
          }
        }
      }

      // Map currency
      if (data.currency) {
        setCurrency(data.currency as Currency);
      }

      // Map line items
      if (data.lineItems && Array.isArray(data.lineItems)) {
        const mappedItems = data.lineItems.map((item: any) => ({
          productServiceId: 0,
          name: item.description || item.name || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || item.price || 0
        }));
        setItems(mappedItems);
      }

      // Map total amount if no line items
      if (data.totalAmount && (!data.lineItems || data.lineItems.length === 0)) {
        setItems([{
          productServiceId: 0,
          name: 'Extracted Item',
          description: 'Item extracted from document',
          quantity: 1,
          unitPrice: data.totalAmount
        }]);
      }

      // Map tax information
      if (data.taxAmount && data.taxAmount > 0) {
        const taxRate = data.totalAmount ? (data.taxAmount / (data.totalAmount - data.taxAmount)) * 100 : 15;
        setTaxes([{
          id: 0,
          taxName: 'Extracted Tax',
          taxRate: taxRate,
          taxAmount: data.taxAmount
        }]);
      }

      // Map notes
      if (data.notes) {
        setNotes(data.notes);
      }

      showToastWithTimeout("success", "✅ Data Applied", "Extracted data has been applied to the form. Please review and modify as needed.", 3000);
    } catch (error) {
      console.error('Error applying extracted data:', error);
      showToastWithTimeout("error", "❌ Application Failed", "Failed to apply extracted data. Please try again.", 3000);
    } finally {
      setIsApplyingExtraction(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Create Quotation</h1>
          <p className="text-gray-600 mt-1">Generate a new quotation for your customer</p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <Select value={currency} onValueChange={(value: Currency) => setCurrency(value)}>
            <SelectTrigger className="w-[120px] border-slate-200 focus:border-blue-500 focus:ring-blue-500">
              <SelectValue placeholder="Currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD ($)</SelectItem>
              <SelectItem value="ZWG">ZWG</SelectItem>
              <SelectItem value="ZAR">ZAR (R)</SelectItem>
              <SelectItem value="MZN">MZN</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={handlePrint}
            disabled={!quotationNumber}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">Print Preview</span>
            <span className="sm:hidden">Print</span>
          </Button>
          
          <Button 
            onClick={handleSubmit} 
            disabled={isSaving || quotationNumber !== ''}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span className="hidden sm:inline">Save Quotation</span>
                <span className="sm:hidden">Save</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main Form with Tabs */}
      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="ai">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Processing
          </TabsTrigger>
          {showAiTab && (
            <TabsTrigger value="extracted">
              Extracted Data
              {extractedData && <span className="ml-2 w-2 h-2 bg-green-500 rounded-full"></span>}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="manual" className="space-y-6">
          {/* Customer Section */}
          <BillToSection
            customerDetails={customerDetails}
            setCustomerDetails={setCustomerDetails}
            customerData={customerData}
            setCustomerData={setCustomerData}
          />

          {/* Customer Tax Information */}
          {customerTaxInfos.length > 0 && (
            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white">
                <CardTitle className="text-lg font-medium text-gray-800">Customer Tax Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {customerTaxInfos.map((taxInfo, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <span className="font-medium mr-2">{taxInfo.taxType?.name || 'Tax'}:</span>
                      <span className="text-gray-600">{taxInfo.taxIdentifier}</span>
                      {taxInfo.isVerified && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                          Verified
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quotation Information */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-background">
              <CardTitle className="text-lg font-medium text-foreground flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Quotation Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="quotationNumber" className="text-sm font-medium text-slate-700">Quotation Number</Label>
                  <Input
                    id="quotationNumber"
                    placeholder="QUO-001"
                    className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    value={quotationNumber}
                    onChange={(e) => setQuotationNumber(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="issueDate" className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    Issue Date
                  </Label>
                  <Input
                    id="issueDate"
                    type="date"
                    className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="expiryDate" className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-amber-500" />
                    Expiry Date
                  </Label>
                  <Input
                    id="expiryDate"
                    type="date"
                    className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product Search Section */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-background">
              <CardTitle className="text-lg font-medium text-foreground flex items-center gap-2">
                <Search className="h-5 w-5 text-primary" />
                Product Search
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className='flex items-center gap-4'>
                <Input
                  type="text"
                  placeholder="Search for product/service"
                  value={searchProduct}
                  onChange={(e) => setSearchProduct(e.target.value)}
                  className="flex-1 border-slate-200 focus:border-primary focus:ring-primary transition-colors"
                />
                <Button onClick={handleSearchProduct} disabled={isSearchingProduct} variant="outline">
                  {isSearchingProduct ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              
              {productData.length > 0 && (
                <select
                  onChange={(e) => {
                    const selectedProduct = productData.find((product) => product.id === Number(e.target.value));
                    if (selectedProduct) {
                      // Check if the last item is empty (default values)
                      const lastItem = items[items.length - 1];
                      const isLastItemEmpty = 
                        lastItem.productServiceId === 0 && 
                        lastItem.name === '' && 
                        lastItem.description === '' && 
                        lastItem.quantity === 1 && 
                        lastItem.unitPrice === 0;
                      
                      const newItems = [...items];
                      
                      if (isLastItemEmpty) {
                        // Replace the empty item
                        newItems[items.length - 1] = {
                          productServiceId: selectedProduct.id,
                          name: selectedProduct.name,
                          description: selectedProduct.description,
                          quantity: 1,
                          unitPrice: selectedProduct.unitPrice
                        };
                      } else {
                        // Append a new item
                        newItems.push({
                          productServiceId: selectedProduct.id,
                          name: selectedProduct.name,
                          description: selectedProduct.description,
                          quantity: 1,
                          unitPrice: selectedProduct.unitPrice
                        });
                      }
                      setItems(newItems);
                      setProductData([]);
                      setSearchProduct('');
                    }
                  }}
                  className="w-full p-2 border border-slate-200 rounded-lg bg-white shadow-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="" disabled selected>Select a product/service</option>
                  {productData.map((product) => (
                    <option
                      key={product.id}
                      value={String(product.id)}
                      className="p-2 border-b border-gray-200 hover:bg-gray-100"
                    >
                      {product.name} (${product.unitPrice})
                    </option>
                  ))}
                </select>
              )}
            </CardContent>
          </Card>

          {/* Items Section */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-background">
              <CardTitle className="text-lg font-medium text-foreground flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Line Items
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item, index) => (
                <Card key={index} className="border border-slate-200 overflow-hidden">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-4 space-y-2">
                        <Label htmlFor={`item-name-${index}`} className="text-sm font-medium text-slate-700">Item Name</Label>
                                                    <Input
                              id={`item-name-${index}`}
                              type="text"
                              placeholder="Product or service name"
                              className="border-slate-200 rounded-lg focus:ring-primary focus:border-primary transition-colors"
                          value={item.name}
                          onChange={(e) => {
                            const newItems = [...items];
                            newItems[index].name = e.target.value;
                            setItems(newItems);
                          }}
                        />
                      </div>
                      
                      <div className="md:col-span-4 space-y-2">
                        <Label htmlFor={`item-desc-${index}`} className="text-sm font-medium text-slate-700">Description</Label>
                        <Textarea
                          id={`item-desc-${index}`}
                          placeholder="Item description"
                          className="min-h-[80px] resize-none border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={item.description}
                          onChange={(e) => {
                            const newItems = [...items];
                            newItems[index].description = e.target.value.slice(0, 255);
                            setItems(newItems);
                          }}
                          maxLength={255}
                        />
                      </div>
                      
                      <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`item-qty-${index}`} className="text-sm font-medium text-slate-700">Quantity</Label>
                          <Input
                            id={`item-qty-${index}`}
                            type="number"
                            min="1"
                            placeholder="Qty"
                            className="border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            value={item.quantity}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index].quantity = Number(e.target.value);
                              setItems(newItems);
                            }}
                          />
                        </div>
                        
                        <div className="space-y-2 col-span-2 md:col-span-1">
                          <Label htmlFor={`item-price-${index}`} className="text-sm font-medium text-slate-700">Unit Price</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                            <Input
                              id={`item-price-${index}`}
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              className="pl-10 border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              value={item.unitPrice}
                              onChange={(e) => {
                                const newItems = [...items];
                                newItems[index].unitPrice = Number(e.target.value);
                                setItems(newItems);
                              }}
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2 md:col-span-1 flex items-end">
                          <Button
                            type="button"
                            onClick={() => {
                              const newItems = items.filter((_, i) => i !== index);
                              setItems(newItems.length ? newItems : [{ productServiceId: 0, name: '', description: '', quantity: 1, unitPrice: 0 }]);
                            }}
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-gray-500 hover:text-red-500"
                          >
                            <Minus size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <Button
                type="button"
                onClick={() => {
                  setItems([...items, { productServiceId: 0, name: '', description: '', quantity: 1, unitPrice: 0 }]);
                }}
                variant="outline"
                className="w-full border-dashed flex items-center justify-center gap-2 py-2 text-gray-600 hover:bg-primary/10 hover:text-primary hover:border-primary/20 transition-colors"
              >
                <Plus size={16} />
                Add Item
              </Button>
            </CardContent>
          </Card>

          {/* Tax Section */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-background">
              <CardTitle className="text-lg font-medium text-foreground flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Taxes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <QuotationTaxSection
                subtotal={calculateSubtotal()}
                currency={currency}
                businessId={selectedBusiness?.id}
                customerId={customerDetails?.id}
                initialTaxes={taxes}
                onTaxesChange={handleTaxesChange}
                isInclusive={isInclusive}
                onInclusiveChange={handleInclusiveChange}
              />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Notes & Terms */}
            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-background">
                <CardTitle className="text-lg font-medium text-foreground">Notes & Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-sm font-medium text-slate-700">Notes to Customer</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any additional notes for the customer"
                    className="min-h-[120px] resize-none border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2 mt-4">
                  <Label htmlFor="terms" className="text-sm font-medium text-slate-700">Terms & Conditions</Label>
                  <Textarea
                    id="terms"
                    placeholder="Add your terms and conditions"
                    className="min-h-[120px] resize-none border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-6">
              {/* Signature Upload */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-background">
                  <CardTitle className="text-lg font-medium text-foreground">Signature</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center bg-white">
                    {signature ? (
                      <div className="relative">
                        <img src={signature} alt="Signature" className="max-h-32 mx-auto" />
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="absolute top-0 right-0 text-slate-500 hover:text-red-500"
                          onClick={() => setSignature(undefined)}
                        >
                          <Minus size={16} />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Upload className="h-8 w-8 text-slate-400" />
                        <p className="text-sm text-slate-600">Upload your signature</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                                                      className="mt-2 hover:bg-primary/10 hover:text-primary hover:border-primary/20 transition-colors"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = handleSignatureUpload as any;
                            input.click();
                          }}
                        >
                          Select File
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Quotation Summary */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3 bg-gradient-to-r from-primary/10 to-background">
                  <CardTitle className="text-lg font-medium text-foreground">Quotation Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 p-6 bg-gradient-to-br from-white to-gray-50 rounded-lg border border-gray-100">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal:</span>
                      <span className="font-medium">{currency} {calculateSubtotal().toFixed(2)}</span>
                    </div>
                    <Separator className="bg-gray-200" />
                    <div className="flex justify-between text-gray-600">
                      <span>Tax:</span>
                      <span className="font-medium">{currency} {calculateTotalTax().toFixed(2)}</span>
                    </div>
                    <Separator className="bg-gray-200" />
                    <div className="flex justify-between text-lg font-semibold text-gray-900">
                      <span>Total:</span>
                                              <span className="text-primary">{currency} {calculateTotal().toFixed(2)}</span>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-gray-200">
                                              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 px-3 py-1">
                          Status: Pending
                        </Badge>
                      {quotationNumber && (
                        <div className="mt-2 text-sm text-gray-500">
                          Quotation Number: <span className="font-medium text-gray-700">{quotationNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3 bg-gradient-to-r from-secondary/50 to-background">
              <CardTitle className="text-lg font-medium text-foreground flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-secondary-foreground" />
                AI-Powered Document Processing
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Upload a quotation document and let our AI extract the data automatically
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <DocumentUpload
                documentType="quotation"
                businessId={selectedBusiness?.id || 1}
                onExtractionComplete={handleExtractionComplete}
                className="w-full"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {showAiTab && (
          <TabsContent value="extracted" className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 bg-gradient-to-r from-accent/50 to-background">
                <CardTitle className="text-lg font-medium text-foreground flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-accent-foreground" />
                  Extracted Data Review
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Review the AI-extracted data and apply it to your quotation form
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                {extractedData ? (
                  <div className="space-y-6">
                    {/* Confidence Score */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <h3 className="font-medium text-blue-900">Extraction Confidence</h3>
                        <p className="text-sm text-blue-700">
                          AI confidence level for the extracted data
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                        {Math.round((extractedData.confidenceScore || 0) * 100)}%
                      </Badge>
                    </div>

                    {/* Extracted Data Preview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-medium text-gray-900">Customer Information</h3>
                        <div className="space-y-2 text-sm">
                          <div><strong>Name:</strong> {extractedData.structuredData?.vendorName || extractedData.structuredData?.customerName || 'Not detected'}</div>
                          <div><strong>Address:</strong> {extractedData.structuredData?.vendorAddress || 'Not detected'}</div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className="font-medium text-gray-900">Quotation Details</h3>
                        <div className="space-y-2 text-sm">
                          <div><strong>Number:</strong> {extractedData.structuredData?.invoiceNumber || extractedData.structuredData?.quotationNumber || 'Not detected'}</div>
                          <div><strong>Date:</strong> {extractedData.structuredData?.invoiceDate || extractedData.structuredData?.quotationDate || 'Not detected'}</div>
                          <div><strong>Total:</strong> {extractedData.structuredData?.totalAmount ? `${extractedData.structuredData.currency || 'USD'} ${extractedData.structuredData.totalAmount}` : 'Not detected'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Line Items */}
                    {extractedData.structuredData?.lineItems && extractedData.structuredData.lineItems.length > 0 && (
                      <div className="space-y-4">
                        <h3 className="font-medium text-gray-900">Line Items</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-gray-200 rounded-lg">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left">Description</th>
                                <th className="px-4 py-2 text-right">Quantity</th>
                                <th className="px-4 py-2 text-right">Unit Price</th>
                                <th className="px-4 py-2 text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {extractedData.structuredData.lineItems.map((item: any, index: number) => (
                                <tr key={index} className="border-t border-gray-200">
                                  <td className="px-4 py-2">{item.description || item.name}</td>
                                  <td className="px-4 py-2 text-right">{item.quantity || 1}</td>
                                  <td className="px-4 py-2 text-right">{item.unitPrice || item.price || 0}</td>
                                  <td className="px-4 py-2 text-right">{item.totalPrice || (item.quantity * item.unitPrice) || 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Apply Button */}
                    <div className="flex justify-end pt-4 border-t border-gray-200">
                      <Button 
                        onClick={applyExtractedData}
                        disabled={isApplyingExtraction}
                        className="bg-accent text-accent-foreground hover:bg-accent/90"
                      >
                        {isApplyingExtraction ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Applying Data...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Apply Extracted Data
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No extracted data available. Please process a document first.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <div className="hidden">
        <PrintableDocument
          ref={printableRef}
          document={{
            id: 'temp-id',
            number: quotationNumber,
            type: 'quotation',
            date: new Date().toLocaleDateString('en-GB'),
            currency,
            items,
            status: 'pending',
            companyDetails,
            customerDetails: {
              name: `${customerDetails.firstName} ${customerDetails.lastName}`,
              address: `${customerDetails.street}, ${customerDetails.city}, ${customerDetails.state}, ${customerDetails.country}`,
              emailAddress: customerDetails.emailAddress,
              companyName: customerDetails.companyName
            },
            notes,
            terms,
            logo,
            signature,
            taxes: taxes.map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: tax.taxAmount,
            })),
            issueDate: issueDate.split('-').reverse().join('/'),
            expiryDate: expiryDate.split('-').reverse().join('/'),
            businessTaxInfos,
            customerTaxInfos  // Add customer tax information to the document
          }}
        />
      </div>
    </div>
  );
}